import NumberFormatPattern from './number-format-pattern';

/**
 * @typedef {import('./number-format').NumberFormatType} NumberFormatType
 * @typedef {import('./number-format-pattern').NumberFormatPatternConstructorOptions} NumberFormatPatternConstructorOptions
 */

/**
 * Number format class
 */
class NumberFormat {
    /**
     * @type {NumberFormatType}
     */
    type;

    /**
     * @type {NumberFormatPattern}
     */
    pattern;

    /**
     * @param {{ pattern: (NumberFormatPattern | undefined | string | NumberFormatPatternConstructorOptions), type: (NumberFormatType | undefined) }} options
     */
    constructor({ pattern = new NumberFormatPattern(), type = NumberFormat.Type.DECIMAL } = {}) {
        let patternInstance = new NumberFormatPattern();
        if (pattern instanceof NumberFormatPattern) {
            patternInstance = pattern;
        }
        if (typeof pattern === 'string') {
            try {
                patternInstance = NumberFormatPattern.fromString(pattern);
            } catch {}
        }
        if (typeof pattern === 'object') {
            try {
                patternInstance = new NumberFormatPattern(pattern);
            } catch {}
        }
        this.pattern = patternInstance;
        this.type = type;
    }

    /**
     * String representation
     * @return {string}
     */
    toString() {
        const { pattern, type } = this;
        return `${pattern.toString()}/${type}`;
    }
}
/**
 * Factory method
 * @param {string} formatString
 * @return {NumberFormat}
 */
NumberFormat.fromString = (formatString) => {
    if (typeof formatString !== 'string') {
        throw new Error(`Invalid number format: ${formatString}`);
    }
    /**
     * @type {[ string, string, NumberFormatType ] | null}
     */
    const match = formatString.match(/^(.*)\/(.+)$/);
    if (match == null) {
        throw new Error(`Failed to parse number format: ${formatString}`);
    }
    const [, patternString, type] = match;
    const pattern = NumberFormatPattern.fromString(patternString);
    return new NumberFormat({ pattern, type });
};

/**
 * @type {import('./number-format').TypeEnum}
 */
NumberFormat.Type = Object.freeze({
    CURRENCY: 'c',
    DECIMAL: 'd'
});

/**
 * @type {import('./number-format').StyleEnum}
 */
NumberFormat.Style = Object.freeze({
    CURRENCY: 'currency',
    DECIMAL: 'decimal',
    PERCENT: 'percent'
});

/**
 * @type {import('./number-format').NotationEnum}
 */
NumberFormat.Notation = Object.freeze({
    COMPACT: 'compact',
    STANDARD: 'standard'
});

/**
 * @type {import('./number-format').CurrencyDisplayEnum}
 */
NumberFormat.CurrencyDisplay = Object.freeze({
    CODE: 'code'
});

export default NumberFormat;
